import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuGroup,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuPortal
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import AuthModal from "@/components/AuthModal";
import ThemeSettings from "@/components/ThemeSettings";
import SessionTimer from "@/components/SessionTimer";
import TwoFactorSetup from "@/components/TwoFactorSetup";
import LanguageSelector from "@/components/LanguageSelector";
import NotificationBell from "@/components/NotificationBell";
import AccessibilityQuickToggle from "@/components/AccessibilityQuickToggle";
import { 
  Menu, Heart, MessageCircle, BookOpen, Stethoscope, Users, Phone, 
  LayoutDashboard, User, LogOut, BookMarked, Video, ClipboardList, 
  MessageSquare, Activity, MapPin, Sparkles, Settings, Palette, Shield, 
  Target, GraduationCap, Command, BarChart3, Building2, UserCheck, 
  Package, ChevronDown, Briefcase, HeartPulse, FileText, 
  MonitorPlay, UserCog, Database, Bell, PhoneIncoming, Sparkles as SparklesIcon, Brain,
  Calendar, Award, UserPlus, ShieldCheck
} from "lucide-react";

// Navigation items with translation keys
const getNavItems = (t) => [
  { path: "/", label: t("nav.home"), icon: Heart },
  { path: "/demo", label: t("nav.demo", "Take the Tour"), icon: Sparkles },
  { path: "/video-library", label: t("nav.videos"), icon: Video },
  { path: "/chat", label: t("nav.chat"), icon: MessageCircle },
  { path: "/symptoms", label: t("nav.symptoms"), icon: Stethoscope },
  { path: "/programs", label: t("nav.programs", "Complementary Services"), icon: Sparkles },
  { path: "/local-resources", label: t("nav.resources"), icon: MapPin },
  { path: "/memory-journal", label: t("nav.memories"), icon: BookOpen, authRequired: true },
  { path: "/journal", label: t("nav.journal"), icon: BookMarked, authRequired: true },
  { path: "/patient-messages", label: t("nav.messages"), icon: MessageSquare, authRequired: true, patientOnly: true },
];

// Command Center menu items organized by category
const commandCenterItems = {
  operations: {
    label: "Operations",
    icon: Briefcase,
    items: [
      { path: "/command", label: "Command Center", icon: Command, description: "Overview dashboard" },
      { path: "/admissions", label: "Admissions Workflow", icon: UserPlus, description: "Referral to admission tracking" },
      { path: "/referrals", label: "Referrals", icon: ClipboardList, description: "Patient referrals" },
      { path: "/approvals", label: "Approvals", icon: UserCheck, description: "User approvals" },
      { path: "/orders", label: "Orders", icon: Package, description: "Medication & equipment" },
      { path: "/admin/activities", label: "All Activities", icon: Activity, description: "Patient activities" },
      { path: "/admin/triage", label: "Call Triage", icon: PhoneIncoming, description: "Call queue management" },
      { path: "/admin/ehr", label: "EHR Documentation", icon: FileText, description: "Patient records & alerts" },
      { path: "/admin/ai-features", label: "AI Features", icon: SparklesIcon, description: "Search, summaries & reminders" },
      { path: "/admin/clinical-intelligence", label: "Clinical Intelligence", icon: Brain, description: "Sentiment & burnout analysis" },
      { path: "/admin/scheduling", label: "Smart Scheduling", icon: Calendar, description: "AI-optimized scheduling" },
      { path: "/ai-hub", label: "AI Experience Hub", icon: SparklesIcon, description: "Goodnight Check, Is This Normal?, NL Command" },
      { path: "/digital-legacy", label: "Digital Legacy", icon: BookOpen, description: "Memory Book & Life Story Companion" },
      { path: "/operations-intelligence", label: "Operations Intelligence", icon: BarChart3, description: "Census, Referrals & Survey Readiness" },
      { path: "/advanced-experience", label: "Advanced Experience", icon: SparklesIcon, description: "Video Visits, NL Ops & Bereavement" },
      { path: "/ambient-documentation", label: "Ambient Documentation", icon: FileText, description: "AI-powered clinical notes from observations" },
      { path: "/command-intelligence", label: "Command Intelligence", icon: ShieldCheck, description: "Proactive alerts & executive cockpit" },
      { path: "/patient-chart", label: "Patient Chart", icon: Stethoscope, description: "Vitals, MAR, orders, assessments" },
      { path: "/workflow-billing", label: "Workflow & Billing", icon: ClipboardList, description: "Visit scheduling, claims & compliance" },
    ]
  },
  analytics: {
    label: "Analytics Dashboards",
    icon: BarChart3,
    items: [
      { path: "/admin/analytics", label: "Analytics Overview", icon: BarChart3, description: "Performance metrics" },
      { path: "/admin/realtime", label: "Real-Time Operations", icon: Activity, description: "Live monitoring" },
      { path: "/admin/predictive", label: "Predictive Analytics", icon: Brain, description: "AI risk predictions" },
      { path: "/admin/quality", label: "Quality Metrics", icon: Award, description: "CAHPS & outcomes" },
    ]
  },
  portals: {
    label: "Portals",
    icon: LayoutDashboard,
    items: [
      { path: "/staff-portal", label: "My Caseload", icon: HeartPulse, description: "Your patients" },
      { path: "/partner-dashboard", label: "Partner Portal", icon: Building2, description: "Partner management" },
      { path: "/physician-portal", label: "Physician Portal", icon: Stethoscope, description: "Physician access" },
    ]
  },
  training: {
    label: "Training & Quality",
    icon: GraduationCap,
    items: [
      { path: "/academy", label: "Hospice Academy", icon: GraduationCap, description: "Training courses" },
      { path: "/ceu-dashboard", label: "CEU Dashboard", icon: BarChart3, description: "Training progress" },
      { path: "/certificates", label: "CEU Certificates", icon: Award, description: "Download certificates" },
      { path: "/quality", label: "Quality Improvement", icon: Target, description: "CAHPS & metrics" },
    ]
  },
  security: {
    label: "Compliance & Security",
    icon: Shield,
    items: [
      { path: "/admin/hipaa", label: "HIPAA Compliance", icon: Shield, description: "PHI protection & audit" },
      { path: "/admin/activities", label: "Activity Monitor", icon: Activity, description: "User activity logs" },
    ]
  },
  admin: {
    label: "Administration",
    icon: UserCog,
    items: [
      { path: "/staff", label: "Staff Dashboard", icon: Users, description: "Staff management" },
      { path: "/admin", label: "Admin Dashboard", icon: Shield, description: "Admin controls" },
      { path: "/system-health", label: "System Health", icon: Activity, description: "System monitoring" },
    ]
  }
};

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);
  const location = useLocation();
  const { user, logout } = useAuth();
  const { t } = useTranslation();

  // Get translated nav items
  const navItems = getNavItems(t);

  const isActive = (path) => location.pathname === path;
  const isPatientOrCaregiver = ["patient", "caregiver", "family"].includes(user?.role?.toLowerCase());
  const filteredNavItems = navItems.filter(item => {
    if (item.authRequired && !user) return false;
    if (item.patientOnly && !isPatientOrCaregiver) return false;
    return true;
  });
  const isAdmin = user?.role === "admin" || user?.role === "administrator";
  const isStaff = ["admin", "administrator", "staff", "nurse", "coordinator"].includes(user?.role?.toLowerCase());

  return (
    <>
      <header className="sticky top-0 z-50 glass-header border-b border-border/50 overflow-x-hidden" data-testid="navbar">
        <div className="container-max px-2 sm:px-6 md:px-12 max-w-full">
          <div className="flex h-14 sm:h-16 items-center justify-between gap-2">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2 group shrink-0" data-testid="navbar-logo">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                <Heart className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
              </div>
              <span className="font-serif text-lg sm:text-xl font-medium text-foreground">{t("app.name")}</span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
              {filteredNavItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                    isActive(item.path)
                      ? "bg-primary/10 text-primary"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted"
                  }`}
                >
                  {item.label}
                </Link>
              ))}
              
              {/* Telehealth Video Call - For staff */}
              {isStaff && (
                <Link to="/telehealth" data-testid="nav-link-telehealth">
                  <Button variant="ghost" size="sm" className="text-green-600 font-medium">
                    <MonitorPlay className="w-4 h-4 mr-1" />
                    Telehealth
                  </Button>
                </Link>
              )}
              
              {/* Command Center Dropdown - For admins */}
              {isAdmin && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="text-primary font-medium" data-testid="command-center-dropdown">
                      <Command className="w-4 h-4 mr-1" />
                      Command Center
                      <ChevronDown className="w-3 h-3 ml-1" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-72">
                    {Object.entries(commandCenterItems).map(([key, category], index) => (
                      <div key={key}>
                        {index > 0 && <DropdownMenuSeparator />}
                        <DropdownMenuLabel className="flex items-center gap-2 text-xs text-muted-foreground uppercase tracking-wider">
                          <category.icon className="w-3 h-3" />
                          {category.label}
                        </DropdownMenuLabel>
                        <DropdownMenuGroup>
                          {category.items.map((item) => (
                            <DropdownMenuItem key={item.path} asChild>
                              <Link 
                                to={item.path} 
                                className="flex items-start gap-3 cursor-pointer"
                                data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                              >
                                <item.icon className="w-4 h-4 mt-0.5 text-muted-foreground" />
                                <div className="flex flex-col">
                                  <span className="font-medium">{item.label}</span>
                                  <span className="text-xs text-muted-foreground">{item.description}</span>
                                </div>
                              </Link>
                            </DropdownMenuItem>
                          ))}
                        </DropdownMenuGroup>
                      </div>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}

              {/* Non-admin staff links */}
              {isStaff && !isAdmin && (
                <>
                  <Link to="/staff-portal" data-testid="nav-link-caseload">
                    <Button variant="ghost" size="sm" className="text-muted-foreground">
                      <HeartPulse className="w-4 h-4 mr-1" />
                      My Caseload
                    </Button>
                  </Link>
                  <Link to="/academy" data-testid="nav-link-academy">
                    <Button variant="ghost" size="sm" className="text-muted-foreground">
                      <GraduationCap className="w-4 h-4 mr-1" />
                      Academy
                    </Button>
                  </Link>
                </>
              )}
            </nav>

            {/* Right Side Items */}
            <div className="flex items-center gap-1 sm:gap-2 shrink-0">
              <div className="hidden sm:block">
                <LanguageSelector />
              </div>
              
              {user ? (
                <>
                  {isAdmin && <div className="hidden md:block"><NotificationBell /></div>}
                  <div className="hidden lg:block"><SessionTimer /></div>
                  <div className="hidden md:block"><AccessibilityQuickToggle /></div>
                  <ThemeSettings />
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="flex items-center gap-1 sm:gap-2 px-1 sm:px-2" data-testid="user-menu-btn">
                        <Avatar className="w-7 h-7 sm:w-8 sm:h-8">
                          <AvatarFallback className="bg-primary/10 text-primary text-xs">
                            {user.name?.charAt(0).toUpperCase() || user.email?.charAt(0).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <span className="hidden md:inline text-sm font-medium">{user.name?.split(' ')[0]}</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56">
                      <DropdownMenuLabel>
                        <div className="flex flex-col">
                          <span className="font-medium">{user.name}</span>
                          <span className="text-xs text-muted-foreground">{user.email}</span>
                          <span className="text-xs text-primary capitalize mt-1">{user.role}</span>
                        </div>
                      </DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem asChild>
                        <Link to="/profile" className="flex items-center gap-2 cursor-pointer">
                          <User className="w-4 h-4" />
                          Profile
                        </Link>
                      </DropdownMenuItem>
                      <DropdownMenuItem asChild>
                        <Link to="/settings" className="flex items-center gap-2 cursor-pointer">
                          <Settings className="w-4 h-4" />
                          {t("common.settings", "Settings")}
                        </Link>
                      </DropdownMenuItem>
                      <TwoFactorSetup />
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={logout} className="text-red-600 cursor-pointer">
                        <LogOut className="w-4 h-4 mr-2" />
                        {t("nav.signOut")}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              ) : (
                <>
                  <AccessibilityQuickToggle />
                  <ThemeSettings />
                  <Button 
                    onClick={() => setAuthOpen(true)} 
                    variant="default" 
                    size="sm"
                    data-testid="signin-btn"
                  >
                    {t("nav.signIn")}
                  </Button>
                </>
              )}

              {/* Mobile Menu */}
              <Sheet open={open} onOpenChange={setOpen}>
                <SheetTrigger asChild className="lg:hidden">
                  <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                    <Menu className="w-5 h-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-80">
                  <div className="flex flex-col gap-4 mt-8">
                    {/* Mobile Nav Items */}
                    {filteredNavItems.map((item) => (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setOpen(false)}
                        className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                          isActive(item.path)
                            ? "bg-primary/10 text-primary"
                            : "text-muted-foreground hover:text-foreground hover:bg-muted"
                        }`}
                      >
                        <item.icon className="w-5 h-5" />
                        {item.label}
                      </Link>
                    ))}
                    
                    {/* Mobile Telehealth */}
                    {isStaff && (
                      <Link
                        to="/telehealth"
                        onClick={() => setOpen(false)}
                        className="flex items-center gap-3 px-4 py-3 rounded-lg text-green-600 hover:bg-green-50"
                      >
                        <MonitorPlay className="w-5 h-5" />
                        Telehealth Video Call
                      </Link>
                    )}
                    
                    {/* Mobile Command Center Items */}
                    {isAdmin && (
                      <>
                        <div className="border-t border-border my-2" />
                        <p className="px-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                          Command Center
                        </p>
                        {Object.values(commandCenterItems).flatMap(category => category.items).map((item) => (
                          <Link
                            key={item.path}
                            to={item.path}
                            onClick={() => setOpen(false)}
                            className={`flex items-center gap-3 px-4 py-2 rounded-lg text-sm transition-colors ${
                              isActive(item.path)
                                ? "bg-primary/10 text-primary"
                                : "text-muted-foreground hover:text-foreground hover:bg-muted"
                            }`}
                          >
                            <item.icon className="w-4 h-4" />
                            {item.label}
                          </Link>
                        ))}
                      </>
                    )}
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </header>

      <AuthModal open={authOpen} onOpenChange={setAuthOpen} />
    </>
  );
}
